import React, { useState, useEffect, useCallback } from 'react';
import './AccountabilityHeader.css';

const formatDate = (dateString) => {
  // Append time so the string is parsed as local midnight, not UTC
  const date = new Date(dateString + 'T00:00:00');
  return date.toLocaleDateString('en-US', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    timeZone: 'America/New_York'
  });
};

const calculateTimeLeft = (endDate) => {
  // endDate is the raw ISO date (YYYY-MM-DD); the period runs to the end of that day.
  const difference = +new Date(endDate + 'T23:59:59') - +new Date();

  if (difference <= 0) {
    return { days: 0, hours: '00', minutes: '00', seconds: '00', expired: true };
  }

  return {
    days: Math.floor(difference / (1000 * 60 * 60 * 24)),
    hours: Math.floor((difference / (1000 * 60 * 60)) % 24).toString().padStart(2, '0'),
    minutes: Math.floor((difference / 1000 / 60) % 60).toString().padStart(2, '0'),
    seconds: Math.floor((difference / 1000) % 60).toString().padStart(2, '0'),
    expired: false
  };
};

// How far through the period we are, 0-100.
const calculateProgress = (startDate, endDate) => {
  const start = +new Date(startDate + 'T00:00:00');
  const end = +new Date(endDate + 'T23:59:59');
  const now = Date.now();
  if (now <= start) return 0;
  if (now >= end) return 100;
  return ((now - start) / (end - start)) * 100;
};

const readStoredPeriod = () => {
  const periodString = localStorage.getItem('period');
  if (!periodString) return null;
  try {
    const { start_date, end_date } = JSON.parse(periodString);
    if (!start_date || !end_date) return null;
    // Keep the raw dates: the countdown and progress bar need parseable ISO values,
    // not the formatted display strings.
    return {
      start_date,
      end_date,
      startLabel: formatDate(start_date),
      endLabel: formatDate(end_date)
    };
  } catch (error) {
    console.error('Error parsing period data from local storage:', error);
    return null;
  }
};

const AccountabilityHeader = () => {
  const [period, setPeriod] = useState(null);
  const [timeLeft, setTimeLeft] = useState({});
  const [progress, setProgress] = useState(0);

  const loadPeriod = useCallback(() => {
    const stored = readStoredPeriod();
    if (stored) {
      setPeriod(stored);
      setTimeLeft(calculateTimeLeft(stored.end_date));
      setProgress(calculateProgress(stored.start_date, stored.end_date));
    }
  }, []);

  useEffect(() => {
    // The period is fetched by the page components, so it may land in localStorage
    // after this header has already mounted. Read it now and again when it arrives.
    loadPeriod();
    window.addEventListener('periodUpdated', loadPeriod);
    return () => window.removeEventListener('periodUpdated', loadPeriod);
  }, [loadPeriod]);

  useEffect(() => {
    if (!period) return undefined;
    const timer = setInterval(() => {
      setTimeLeft(calculateTimeLeft(period.end_date));
      setProgress(calculateProgress(period.start_date, period.end_date));
    }, 1000);
    return () => clearInterval(timer);
  }, [period]);

  const units = [
    { value: timeLeft.days, label: 'Days' },
    { value: timeLeft.hours, label: 'Hours' },
    { value: timeLeft.minutes, label: 'Min' },
    { value: timeLeft.seconds, label: 'Sec' }
  ];

  return (
    <div className="hero">
      <div className="hero-inner">

        <div className="hero-identity">
          <span className="hero-eyebrow">Wehaa Accountability</span>
          <h2 className="hero-title">A quantitative framework for optimizing company performance</h2>
          {period && (
            <span className="hero-period">
              {period.startLabel} <span className="hero-period-sep">→</span> {period.endLabel}
            </span>
          )}
        </div>

        {period ? (
          <div className="hero-countdown">
            <span className="hero-countdown-label">
              {timeLeft.expired ? 'Period closed' : 'Time remaining in period'}
            </span>
            {/* aria-hidden: this ticks every second and would otherwise be an
                unstoppable screen-reader announcement loop. */}
            <div className="hero-units" aria-hidden="true">
              {units.map((unit, i) => (
                <React.Fragment key={unit.label}>
                  {i > 0 && <span className="hero-unit-sep">:</span>}
                  <div className="hero-unit">
                    <span className="hero-unit-value">{unit.value}</span>
                    <span className="hero-unit-label">{unit.label}</span>
                  </div>
                </React.Fragment>
              ))}
            </div>
            <span className="visually-hidden">
              {timeLeft.days} days remaining in the current reporting period.
            </span>
          </div>
        ) : (
          <div className="hero-countdown">
            <span className="hero-countdown-label">No reporting period set</span>
          </div>
        )}

      </div>

      {period && (
        <div
          className="hero-progress"
          role="progressbar"
          aria-valuenow={Math.round(progress)}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-label="Progress through the current reporting period"
        >
          <div className="hero-progress-fill" style={{ width: `${progress}%` }} />
        </div>
      )}
    </div>
  );
};

export default AccountabilityHeader;
